import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Power, Search, ShoppingCart, Package,
  ArrowDownToLine, ClipboardList, Camera, Trash2,
  Send, RefreshCw, ChevronLeft, User,
  X, Check, AlertTriangle, PackageOpen, Smartphone
} from 'lucide-react';
import { API_URL, API_HOST } from '../config/api';
import logService from '../services/logService';
import useEscapeClose from '../hooks/useEscapeClose';
import './TerminalPanel.css';

const IMAGE_HOST = API_HOST;

const TerminalPanel = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('price'); // 'price', 'cart', 'products', 'count', 'transfers'
  const barcodeInputRef = useRef(null);
  const countBarcodeRef = useRef(null);

  // Fiyat sorgulama state'leri
  const [barcode, setBarcode] = useState('');
  const [searchedProduct, setSearchedProduct] = useState(null);
  const [searching, setSearching] = useState(false);

  // Sepet state'leri
  const [cart, setCart] = useState([]);
  const [sendingCart, setSendingCart] = useState(false);

  // Aktif kampanyalar (basit yüzde/kategori/ürün indirimleri - el terminalinde kompleks kampanya desteği yok)
  const [activeCampaigns, setActiveCampaigns] = useState([]);

  // Satış sorumlusu state'leri
  const [salesStaffList, setSalesStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);

  // Ürün listesi state'leri
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetail, setShowProductDetail] = useState(false);

  // Stok güncelleme
  const [stockUpdateValue, setStockUpdateValue] = useState('');

  // Fiyat bilgileri
  const [prices, setPrices] = useState({
    USD: 0, EUR: 0,
    USD_alis: 0, USD_satis: 0,
    EUR_alis: 0, EUR_satis: 0,
    altin: 0, altin_alis: 0, altin_satis: 0,
    gumus: 0, gumus_alis: 0, gumus_satis: 0
  });

  // Manuel/özel kurlar
  const [customRates, setCustomRates] = useState({});

  // Sayim state'leri
  const [countInvite, setCountInvite] = useState(null);
  const [countSession, setCountSession] = useState(null);
  const [countBarcode, setCountBarcode] = useState('');
  const [countInventoryData, setCountInventoryData] = useState({});
  const [lastCountedProduct, setLastCountedProduct] = useState(null);
  const [countProducts, setCountProducts] = useState([]);
  const [excessAdditions, setExcessAdditions] = useState({}); // Stoğa eklenecek fazlalar
  const [showExcessModal, setShowExcessModal] = useState(false);
  const [pendingExcessProduct, setPendingExcessProduct] = useState(null);
  const [showCountSummary, setShowCountSummary] = useState(false);
  const [finalizingCount, setFinalizingCount] = useState(false);

  // Fotoğraf çekim isteği state'leri
  const [photoRequest, setPhotoRequest] = useState(null);
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [photoStream, setPhotoStream] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Transfer teslim alma state'leri
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [loadingTransfers, setLoadingTransfers] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [transferItems, setTransferItems] = useState([]);
  const [receivingTransfer, setReceivingTransfer] = useState(false);
  const [scannedTransferItems, setScannedTransferItems] = useState({}); // {product_id: scanned_count}
  const [transferBarcode, setTransferBarcode] = useState('');
  const [lastScannedTransferItem, setLastScannedTransferItem] = useState(null);
  const transferBarcodeRef = useRef(null);

  // Genel onay modalı (window.confirm yerine)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Evet',
    cancelText: 'İptal',
    type: 'warning',
    onConfirm: () => {}
  });

  // ESC tuşu ile modal kapatma
  useEscapeClose([
    [showConfirmModal, () => setShowConfirmModal(false)],
    [showPhotoCapture, () => setShowPhotoCapture(false)],
    [showCountSummary, () => setShowCountSummary(false)],
    [showExcessModal, () => setShowExcessModal(false)],
    [showProductDetail, () => setShowProductDetail(false)],
    [showStaffModal, () => setShowStaffModal(false)],
  ]);

  // Genel onay modal'ı gösterme fonksiyonu
  const showConfirmation = (config) => {
    setConfirmModalConfig({
      title: config.title || 'Onay',
      message: config.message || '',
      confirmText: config.confirmText || 'Evet',
      cancelText: config.cancelText || 'İptal',
      onConfirm: config.onConfirm || (() => {}),
      type: config.type || 'warning'
    });
    setShowConfirmModal(true);
  };

  // Sayfa yüklendiğinde barkod input'una odaklan
  useEffect(() => {
    if (activeTab === 'price' && barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
    if (activeTab === 'count' && countBarcodeRef.current) {
      countBarcodeRef.current.focus();
    }
  }, [activeTab]);

  // Bekleyen transferleri periyodik kontrol et (her 30 saniye)
  useEffect(() => {
    if (!user?.store_id) return;

    const checkTransfers = async () => {
      try {
        const response = await fetch(`${API_URL}/transfers/pending/${user.store_id}`);
        const data = await response.json();
        setPendingTransfers(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Transfer kontrol hatası:', error);
      }
    };

    checkTransfers();
    const interval = setInterval(checkTransfers, 30000);
    return () => clearInterval(interval);
  }, [user?.store_id]);

  // Mağazaya ait aktif kampanyaları getir (sepette basit %10 vb. indirimlerin uygulanabilmesi için)
  useEffect(() => {
    if (!user?.store_id) return;

    const fetchActiveCampaigns = async () => {
      try {
        const response = await fetch(`${API_URL}/campaigns/active?store_id=${user.store_id}`);
        const data = await response.json();
        setActiveCampaigns(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Kampanya yükleme hatası:', error);
        setActiveCampaigns([]);
      }
    };

    fetchActiveCampaigns();
  }, [user?.store_id]);

  // Barkod değiştiğinde otomatik arama yap (debounce ile)
  useEffect(() => {
    if (!barcode.trim() || barcode.length < 3) return;

    const timeout = setTimeout(() => {
      // Otomatik arama tetikle
      searchByBarcodeAuto(barcode.trim());
    }, 300);

    return () => clearTimeout(timeout);
  }, [barcode]);

  // Sayım daveti kontrolü (her 5 saniyede bir)
  useEffect(() => {
    if (!user?.id) return;

    const checkInvites = async () => {
      try {
        const response = await fetch(`${API_URL}/inventory-sessions/pending/${user.id}`);
        const data = await response.json();
        console.log('Davet kontrolü, user.id:', user.id, 'response:', data);
        // API array döndürüyor, ilk daveti al
        const invite = Array.isArray(data) ? data[0] : data;
        if (invite && invite.session_id) {
          console.log('Davet bulundu:', invite);
          setCountInvite(invite);
          // Otomatik olarak sayım sekmesine geç
          if (!countSession) {
            setActiveTab('count');
          }
        }
      } catch (error) {
        console.error('Davet kontrol hatası:', error);
      }
    };

    checkInvites();
    const interval = setInterval(checkInvites, 5000);
    return () => clearInterval(interval);
  }, [user?.id, countSession]);

  // Fotoğraf isteği kontrolü (her 3 saniyede bir)
  useEffect(() => {
    if (!user?.id) return;

    const checkPhotoRequests = async () => {
      try {
        const response = await fetch(`${API_URL}/terminal-photo-requests/pending/${user.id}`);
        const data = await response.json();
        if (data && data.id && !photoRequest) {
          console.log('Fotoğraf isteği bulundu:', data);
          setPhotoRequest(data);
        }
      } catch (error) {
        console.error('Fotoğraf isteği kontrol hatası:', error);
      }
    };

    checkPhotoRequests();
    const interval = setInterval(checkPhotoRequests, 3000);
    return () => clearInterval(interval);
  }, [user?.id, photoRequest]);

  // Aktif sayım oturumu verilerini senkronize et (her 3 saniyede)
  useEffect(() => {
    if (!countSession?.id) return;

    const syncData = async () => {
      try {
        const response = await fetch(`${API_URL}/inventory-sessions/${countSession.id}`);
        const data = await response.json();
        if (data && data.inventory_data) {
          setCountInventoryData(prev => {
            const merged = { ...prev };
            Object.entries(data.inventory_data).forEach(([key, value]) => {
              if (merged[key] === undefined || merged[key] < value) {
                merged[key] = value;
              }
            });
            return merged;
          });
        }
      } catch (error) {
        console.error('Sync hatası:', error);
      }
    };

    const interval = setInterval(syncData, 3000);
    return () => clearInterval(interval);
  }, [countSession?.id]);

  // Sayım verisini sunucuya gönder (anlık - gerçek zamanlı senkronizasyon)
  useEffect(() => {
    if (!countSession?.id || Object.keys(countInventoryData).length === 0) return;

    // 50ms debounce - neredeyse anlık gönderim
    const timeout = setTimeout(async () => {
      try {
        await fetch(`${API_URL}/inventory-sessions/${countSession.id}/data`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            inventory_data: countInventoryData,
            source: `terminal_${user?.id}`
          })
        });
      } catch (error) {
        console.error('Veri gönderme hatası:', error);
      }
    }, 50);

    return () => clearTimeout(timeout);
  }, [countInventoryData, countSession?.id, user?.id]);

  // Ürünleri sayım için yükle
  useEffect(() => {
    if (activeTab === 'count' && countProducts.length === 0 && (countSession?.store_id || user?.store_id)) {
      fetchCountProducts(countSession?.store_id || user?.store_id);
    }
  }, [activeTab, countSession?.store_id]);

  // Özel kurları getir
  const fetchCustomRates = async () => {
    try {
      const response = await fetch(`${API_URL}/custom-rates`);
      if (response.ok) {
        const data = await response.json();
        const ratesObj = {};
        data.forEach(rate => {
          ratesObj[rate.currency] = rate;
        });
        setCustomRates(ratesObj);
      }
    } catch (error) {
      console.error('Özel kur getirme hatası:', error);
    }
  };

  // Fiyatları ve özel kurları getir
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await fetch(`${API_URL}/prices`);
        const data = await response.json();
        setPrices(data);
      } catch (error) {
        console.error('Fiyatlar yüklenemedi:', error);
      }
    };
    fetchPrices();
    fetchCustomRates();
    const interval = setInterval(() => {
      fetchPrices();
      fetchCustomRates();
    }, 60000); // Her dakika güncelle
    return () => clearInterval(interval);
  }, []);

  // Barkod ile ürün ara (form submit için)
  const searchByBarcode = async (e) => {
    e?.preventDefault();
    if (!barcode.trim()) return;
    await searchByBarcodeAuto(barcode.trim());
  };

  // Otomatik barkod arama (debounce ile tetiklenir)
  const searchByBarcodeAuto = async (barcodeValue) => {
    if (!barcodeValue || searching) return;

    setSearching(true);
    setSearchedProduct(null);

    try {
      const response = await fetch(`${API_URL}/products/barcode/${barcodeValue}`);
      if (response.ok) {
        const data = await response.json();
        setSearchedProduct(data);
      } else {
        toast.error('Ürün bulunamadı!');
        setSearchedProduct(null);
      }
    } catch (error) {
      console.error('Arama hatası:', error);
      toast.error('Arama sırasında hata oluştu!');
    } finally {
      setSearching(false);
    }
  };

  // Yeni sorgulama
  const clearSearch = () => {
    setBarcode('');
    setSearchedProduct(null);
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  };

  // Ayar katsayıları (altın fiyat hesaplaması için)
  const karatMultipliers = {
    '8': 333,
    '9': 375,
    '10': 417,
    '14': 585,
    '18': 750,
    '21': 875,
    '22': 916,
    '24-995': 995,
    '24-999': 999
  };

  // Döviz kurunu TL'ye çevir (önce manuel kur, yoksa piyasa kuru)
  const convertToTRY = (amount, currency) => {
    const curr = (currency || 'TRY').toUpperCase();
    if (curr === 'TRY') return amount;

    // Önce manuel/özel kuru kontrol et
    const customRate = customRates[curr]?.rate;
    if (customRate) {
      return amount * parseFloat(customRate);
    }
    // Manuel kur yoksa piyasa kurunu kullan
    if (curr === 'USD') {
      const rate = parseFloat(prices?.USD_satis) || parseFloat(prices?.USD) || 1;
      return amount * rate;
    }
    if (curr === 'EUR') {
      const rate = parseFloat(prices?.EUR_satis) || parseFloat(prices?.EUR) || 1;
      return amount * rate;
    }
    return amount;
  };

  // Aktif kuru getir (manuel varsa onu, yoksa piyasa)
  const getActiveRate = (currency) => {
    if (customRates[currency]?.rate) {
      return { rate: parseFloat(customRates[currency].rate), isCustom: true };
    }
    if (currency === 'USD') {
      return { rate: parseFloat(prices?.USD_satis) || parseFloat(prices?.USD) || 0, isCustom: false };
    }
    if (currency === 'EUR') {
      return { rate: parseFloat(prices?.EUR_satis) || parseFloat(prices?.EUR) || 0, isCustom: false };
    }
    return { rate: 1, isCustom: false };
  };

  // Ürün fiyat hesaplama (altın + döviz desteği)
  const calculateGoldPrice = (product) => {
    // Sabit fiyatlı ürünler için para birimi dönüşümü yap
    if (product.pricing_type === 'fixed') {
      const price = Number(product.sell_price) || 0;
      if (product.currency === 'USD') {
        const usdRate = customRates['USD']?.rate_satis || customRates['USD']?.rate || parseFloat(prices?.USD_satis) || 1;
        return price * usdRate;
      } else if (product.currency === 'EUR') {
        const eurRate = customRates['EUR']?.rate_satis || customRates['EUR']?.rate || parseFloat(prices?.EUR_satis) || 1;
        return price * eurRate;
      }
      return price; // TRY ise direkt döndür
    }

    // Altın ürünleri için özel hesaplama
    if (product.metal_type === 'gold' && product.weight && product.gold_karat) {
      // Önce custom rate kontrol et, sonra piyasa fiyatı
      const goldGramPrice = customRates['GOLD']?.rate || parseFloat(prices?.altin_satis) || 0;
      if (goldGramPrice <= 0) {
        // Altın fiyatı yoksa döviz kontrolü yap
        const basePrice = parseFloat(product.sell_price) || 0;
        const currency = product.currency || product.price_currency || 'TRY';
        return convertToTRY(basePrice, currency);
      }

      const gram = parseFloat(product.weight) || 0;
      const labor = parseFloat(product.labor_cost) || 0;
      const multiplier = karatMultipliers[product.gold_karat] || 585; // Varsayılan 14 ayar

      // Formül: ((ayar_katsayısı + işçilik) × gram / 1000) × altın_gram_fiyatı
      const laboredGram = (multiplier + labor) * gram / 1000;
      const calculatedPrice = laboredGram * goldGramPrice;

      return calculatedPrice;
    }

    // Diğer ürünler için döviz kuru kontrolü
    const basePrice = parseFloat(product.sell_price) || 0;
    const currency = product.currency || product.price_currency || 'TRY';
    return convertToTRY(basePrice, currency);
  };

  // Gümüş fiyat hesaplama
  const calculateSilverPrice = (product) => {
    // Sabit fiyatlı ürünler için para birimi dönüşümü yap
    if (product.pricing_type === 'fixed') {
      const price = Number(product.sell_price) || 0;
      if (product.currency === 'USD') {
        const usdRate = customRates['USD']?.rate_satis || customRates['USD']?.rate || parseFloat(prices?.USD_satis) || 1;
        return price * usdRate;
      } else if (product.currency === 'EUR') {
        const eurRate = customRates['EUR']?.rate_satis || customRates['EUR']?.rate || parseFloat(prices?.EUR_satis) || 1;
        return price * eurRate;
      }
      return price; // TRY ise direkt döndür
    }

    if (product.metal_type !== 'silver' || !product.weight) {
      const basePrice = parseFloat(product.sell_price) || 0;
      const currency = product.currency || product.price_currency || 'TRY';
      return convertToTRY(basePrice, currency);
    }

    // Önce custom rate kontrol et, sonra piyasa fiyatı
    const silverGramPrice = customRates['SILVER']?.rate || parseFloat(prices?.gumus_satis) || 0;
    const usdRate = customRates['USD']?.rate_satis || customRates['USD']?.rate || parseFloat(prices?.USD_satis) || 0;

    if (silverGramPrice <= 0) {
      toast.warning('Gümüş fiyatı alınamadı, kayıtlı fiyat kullanılıyor');
      const basePrice = parseFloat(product.sell_price) || 0;
      const currency = product.currency || product.price_currency || 'TRY';
      return convertToTRY(basePrice, currency);
    }

    const gram = parseFloat(product.weight) || 0;
    const purity = parseInt(product.silver_purity) || 925;
    const profitMargin = parseFloat(product.profit_margin) || 3;
    const laborPerGram = parseFloat(product.labor_cost) || 0;

    // Has gümüş gramı: (ürün gramı × ayar) / 1000
    const pureGram = (gram * purity) / 1000;

    // Gümüş maliyeti (TL): has gram × gümüş kuru
    const silverCost = pureGram * silverGramPrice;

    // İşçilik maliyeti (TL): ürün gramı × gram başı işçilik × dolar kuru
    const laborCostTL = gram * laborPerGram * usdRate;

    // Toplam maliyet (TL): gümüş maliyeti + işçilik maliyeti
    const totalCostTL = silverCost + laborCostTL;

    // Satış fiyatı (TL): toplam maliyet × kar oranı
    const salePriceTL = totalCostTL * profitMargin;

    return salePriceTL;
  };

  // Fiyat sorgulamada gösterilecek hesaplanmış fiyat
  const displayPrice = useMemo(() => {
    if (!searchedProduct) return 0;

    if (searchedProduct.metal_type === 'silver' && searchedProduct.weight) {
      return calculateSilverPrice(searchedProduct);
    }
    return calculateGoldPrice(searchedProduct);
  }, [searchedProduct, prices, customRates]);

  // Sepete ürün ekle
  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);

    if (existingItem) {
      setCart(cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
      toast.success('Ürün miktarı artırıldı!');
    } else {
      // Metal türüne göre fiyat hesapla
      const isGoldProduct = product.metal_type === 'gold';
      const isSilverProduct = product.metal_type === 'silver';

      let calculatedPrice;
      if (isGoldProduct) {
        calculatedPrice = calculateGoldPrice(product);
      } else if (isSilverProduct) {
        calculatedPrice = calculateSilverPrice(product);
      } else {
        calculatedPrice = calculateGoldPrice(product); // Döviz dönüşümü için
      }

      setCart([...cart, {
        id: product.id,
        name: product.name,
        barcode: product.barcode,
        sell_price: calculatedPrice,
        image: product.image,
        quantity: 1,
        isGoldProduct: isGoldProduct,
        isSilverProduct: isSilverProduct,
        goldKarat: product.gold_karat,
        weight: product.weight,
        laborCost: product.labor_cost,
        category: product.category,
        metal_type: product.metal_type,
        brand: product.brand
      }]);
      toast.success('Ürün sepete eklendi!');
    }
  };

  // Sepetten ürün çıkar
  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
    toast.info('Ürün sepetten çıkarıldı');
  };

  // Sepet miktarını güncelle
  const updateCartQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }
    setCart(cart.map(item =>
      item.id === productId
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  // Sepet toplamı
  const cartTotal = cart.reduce((sum, item) => sum + (item.sell_price * item.quantity), 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Sepete uygulanabilecek en iyi basit kampanyayı bul (yüzde/kategori/ürün indirimi)
  // Not: El terminalinde kompleks kampanya türleri (X al Y öde, kademeli, hediye, set) desteklenmez;
  // sadece basit yüzde indirimleri (ör. piercing %10) hesaplanır.
  const { appliedCampaign, campaignDiscount } = useMemo(() => {
    if (cart.length === 0 || activeCampaigns.length === 0) {
      return { appliedCampaign: null, campaignDiscount: 0 };
    }

    const parseJson = (val) => {
      try {
        return typeof val === 'string' ? JSON.parse(val || '{}') : (val || {});
      } catch (e) {
        return {};
      }
    };

    let bestCampaign = null;
    let bestDiscount = 0;

    activeCampaigns.forEach(campaign => {
      if (!['percentage', 'category_discount', 'product_discount'].includes(campaign.campaign_type)) return;

      const rewards = parseJson(campaign.rewards);
      const conditions = parseJson(campaign.conditions);
      const discountPercent = rewards.discount_percent || rewards.percentage || 0;
      if (discountPercent <= 0) return;

      let scopeItems;
      if (campaign.campaign_type === 'product_discount') {
        const productList = conditions.products || [];
        scopeItems = cart.filter(item => productList.length === 0 || productList.includes(item.id));
      } else {
        const metalTypes = conditions.metal_types || [];
        const categories = conditions.categories || [];
        const brands = conditions.brands || [];
        scopeItems = cart.filter(item => {
          const metalMatch = metalTypes.length === 0 || metalTypes.includes(item.metal_type);
          const catMatch = categories.length === 0 || categories.includes(item.category);
          const brandMatch = brands.length === 0 || brands.includes(item.brand);
          return metalMatch && catMatch && brandMatch;
        });
      }
      if (scopeItems.length === 0) return;

      const scopeTotal = scopeItems.reduce((sum, item) => sum + (item.sell_price * item.quantity), 0);
      const minPurchase = conditions.min_purchase || conditions.min_amount || 0;
      if (minPurchase > 0 && scopeTotal < minPurchase) return;

      const discount = scopeTotal * discountPercent / 100;
      if (discount > bestDiscount) {
        bestDiscount = discount;
        bestCampaign = campaign;
      }
    });

    return { appliedCampaign: bestCampaign, campaignDiscount: bestDiscount };
  }, [cart, activeCampaigns]);

  const cartTotalAfterDiscount = Math.max(0, cartTotal - campaignDiscount);

  // Sepeti temizle
  const clearCart = () => {
    if (cart.length === 0) return;
    showConfirmation({
      title: 'Sepeti Temizle',
      message: 'Sepeti temizlemek istediğinize emin misiniz?',
      confirmText: 'Evet, Temizle',
      cancelText: 'İptal',
      type: 'warning',
      onConfirm: () => {
        setCart([]);
        toast.info('Sepet temizlendi');
      }
    });
  };

  // Sepeti kasiyere gönder
  const sendCartToCashier = async () => {
    if (cart.length === 0) {
      toast.warning('Sepet boş!');
      return;
    }

    if (!user?.store_id) {
      toast.error('Mağaza bilgisi bulunamadı!');
      return;
    }

    setSendingCart(true);
    try {
      console.log('Kasiyere gönderiliyor:', { store_id: user.store_id, user_id: user.id, cart_items: cart.length });

      const response = await fetch(`${API_URL}/terminal-carts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_id: user.store_id,
          terminal_user_id: user.id,
          terminal_user_name: user.name,
          items: cart,
          total_amount: cartTotalAfterDiscount,
          item_count: cartItemCount,
          sales_staff_id: selectedStaff?.id || null,
          sales_staff_name: selectedStaff?.name || null,
          discount_amount: campaignDiscount > 0 ? campaignDiscount : 0,
          discount_type: campaignDiscount > 0 ? 'amount' : null,
          campaign_id: campaignDiscount > 0 ? (appliedCampaign?.id || null) : null,
          campaign_name: campaignDiscount > 0 ? (appliedCampaign?.name || null) : null
        })
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error:', errorText);
        toast.error(`Sunucu hatası: ${response.status}`);
        return;
      }

      const data = await response.json();
      if (data.success) {
        toast.success('Sepet kasiyere gönderildi!');
        setCart([]);
        setSelectedStaff(null);
        // Sonraki sepet için güncel varsayılanı yeniden uygula (Ayarlar'dan kaldırıldıysa boş kalır)
        fetchSalesStaff({ applyDefault: true });
        setActiveTab('price');
      } else {
        toast.error(data.message || 'Gönderim başarısız!');
      }
    } catch (error) {
      console.error('Sepet gönderme hatası:', error);
      toast.error(`Bağlantı hatası: ${error.message}`);
    } finally {
      setSendingCart(false);
    }
  };

  // Varsayılan satış sorumlusu: localStorage'daki user bayat olabilir (Ayarlar'dan
  // kaldırılsa bile terminal yeniden açılana kadar eski değer kalıyordu), her seferinde sunucudan oku.
  const defaultStaffIdRef = useRef(user?.default_sales_staff_id || null);

  const fetchCurrentDefaultStaffId = async () => {
    try {
      const res = await fetch(`${API_URL}/auth/verify`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        defaultStaffIdRef.current = data?.user?.default_sales_staff_id || null;
      }
    } catch (e) {
      // Ağ hatasında son bilinen değerle devam
    }
    return defaultStaffIdRef.current;
  };

  // Satış sorumlularını getir (sadece bu mağazanın personeli)
  const fetchSalesStaff = async ({ applyDefault = false } = {}) => {
    setLoadingStaff(true);
    try {
      const [response, defaultId] = await Promise.all([
        fetch(`${API_URL}/sales-staff${user?.store_id ? `?store_id=${user.store_id}` : ''}`),
        fetchCurrentDefaultStaffId()
      ]);
      const data = await response.json();
      // Sadece aktif satış sorumlularını filtrele
      const activeStaff = (Array.isArray(data) ? data : []).filter(s => s.active !== 0);
      setSalesStaffList(activeStaff);

      const defaultStaff = defaultId ? activeStaff.find(s => String(s.id) === String(defaultId)) : null;
      setSelectedStaff(prev => {
        // Seçili kişi bu mağazanın listesinde yoksa düşür
        const stillValid = prev && activeStaff.some(s => String(s.id) === String(prev.id));
        if (applyDefault) return defaultStaff || null;
        return stillValid ? prev : null;
      });
    } catch (error) {
      console.error('Satış sorumluları yüklenemedi:', error);
    } finally {
      setLoadingStaff(false);
    }
  };

  // Sayfa yüklendiğinde varsayılan satış sorumlusunu (varsa) uygula
  useEffect(() => {
    fetchSalesStaff({ applyDefault: true });
  }, [user?.store_id]);

  // Ürünleri getir (mağaza bazlı filtreleme)
  const fetchProducts = async () => {
    if (!user?.store_id) {
      console.warn('Store ID bulunamadı');
      return;
    }
    setLoadingProducts(true);
    try {
      const response = await fetch(`${API_URL}/products/store/${user.store_id}`);
      const data = await response.json();
      // API obje döndürüyorsa products arrayini al, değilse doğrudan kullan
      setProducts(Array.isArray(data) ? data : data.products || []);
    } catch (error) {
      console.error('Ürünler yüklenemedi:', error);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Sayım için ürünleri getir (mağaza bazlı)
  const fetchCountProducts = async (storeId) => {
    const targetStoreId = storeId || user?.store_id;
    if (!targetStoreId) return;

    try {
      // Mağaza bazlı ürünleri getir (mağazanın stoğuyla)
      const response = await fetch(`${API_URL}/products/store/${targetStoreId}`);
      const data = await response.json();
      // API obje döndürüyorsa products arrayini al, değilse doğrudan kullan
      setCountProducts(Array.isArray(data) ? data : data.products || []);
    } catch (error) {
      console.error('Sayım ürünleri yüklenemedi:', error);
      setCountProducts([]);
    }
  };

  // Sayım ses efekti (localStorage'dan ayar kontrolü)
  const playCountSound = (type) => {
    // Ses ayarını kontrol et - false ise veya 'false' string ise ses çalma
    try {
      const soundEnabled = localStorage.getItem('soundEnabled');
      if (soundEnabled === 'false' || soundEnabled === false) {
        return;
      }
    } catch (e) {
      // localStorage erişim hatası - devam et
    }

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      if (type === 'success') {
        oscillator.frequency.value = 1200;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.3;
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.1);
      } else {
        oscillator.frequency.value = 400;
        oscillator.type = 'square';
        gainNode.gain.value = 0.2;
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.15);

        setTimeout(() => {
          const osc2 = audioContext.createOscillator();
          const gain2 = audioContext.createGain();
          osc2.connect(gain2);
          gain2.connect(audioContext.destination);
          osc2.frequency.value = 300;
          osc2.type = 'square';
          gain2.gain.value = 0.2;
          osc2.start();
          osc2.stop(audioContext.currentTime + 0.15);
        }, 180);
      }
    } catch (error) {
      console.error('Ses çalma hatası:', error);
    }
  };

  // Sayım davetini kabul et
  const acceptCountInvite = async () => {
    if (!countInvite) return;

    try {
      const response = await fetch(`${API_URL}/inventory-session-terminals/${countInvite.id}/accept`, {
        method: 'PUT'
      });
      const data = await response.json();

      if (data.success) {
        setCountSession({
          id: countInvite.session_id,
          terminal_id: countInvite.id,
          store_id: countInvite.store_id // Mağaza ID'sini sakla
        });
        setCountInvite(null);
        toast.success('Sayıma katıldınız!');
        // Mağaza bazlı ürünleri getir
        fetchCountProducts(countInvite.store_id);
      }
    } catch (error) {
      console.error('Davet kabul hatası:', error);
      toast.error('Bir hata oluştu!');
    }
  };

  // Sayım barkod okutma
  const handleCountBarcode = (e) => {
    if (e.key !== 'Enter' || !countBarcode.trim()) return;

    const term = countBarcode.trim();
    const product = countProducts.find(p =>
      p.barcode === term || p.sku?.toLowerCase() === term.toLowerCase()
    );

    if (product) {
      const currentCount = countInventoryData[product.id] || 0;
      const excessCount = excessAdditions[product.id] || 0;
      const newCount = currentCount + 1;

      if (newCount > product.stock + excessCount) {
        // Fazla okutuldu - modal göster
        playCountSound('error');
        setPendingExcessProduct({
          ...product,
          currentCount: currentCount,
          newCount: newCount,
          excessAmount: newCount - product.stock
        });
        setShowExcessModal(true);
        setLastCountedProduct({
          ...product,
          counted: currentCount,
          status: 'excess',
          message: `FAZLA! (${currentCount}/${product.stock})`
        });
      } else if (newCount === product.stock + excessCount) {
        // Tam sayıda
        playCountSound('success');
        setCountInventoryData(prev => ({ ...prev, [product.id]: newCount }));
        setLastCountedProduct({
          ...product,
          counted: newCount,
          status: 'match',
          message: `✓ Tamam (${newCount}/${product.stock}${excessCount > 0 ? '+' + excessCount : ''})`
        });
      } else {
        // Normal okutma
        playCountSound('success');
        setCountInventoryData(prev => ({ ...prev, [product.id]: newCount }));
        setLastCountedProduct({
          ...product,
          counted: newCount,
          status: 'counting',
          message: `${newCount}/${product.stock}${excessCount > 0 ? '+' + excessCount : ''}`
        });
      }
    } else {
      // Ürün bulunamadı - sistemde kayıtlı değil
      playCountSound('error');
      setLastCountedProduct({
        name: term,
        barcode: term,
        status: 'notfound',
        message: 'SİSTEMDE KAYITLI DEĞİL!'
      });
      toast.error(`"${term}" barkodlu ürün sistemde bulunamadı!`);
    }

    setCountBarcode('');
    setTimeout(() => {
      if (countBarcodeRef.current) {
        countBarcodeRef.current.focus();
      }
    }, 100);

    // 5 saniye sonra ürün bilgisini temizle
    setTimeout(() => {
      setLastCountedProduct(null);
    }, 5000);
  };

  // Fazla ürünü stoğa ekle
  const handleExcessApprove = () => {
    if (!pendingExcessProduct) return;

    const product = pendingExcessProduct;
    // Fazlayı kaydet
    setExcessAdditions(prev => ({
      ...prev,
      [product.id]: (prev[product.id] || 0) + 1
    }));
    // Sayımı güncelle
    setCountInventoryData(prev => ({
      ...prev,
      [product.id]: product.newCount
    }));

    playCountSound('success');
    toast.success('Fazla ürün stoğa eklenecek olarak işaretlendi');
    setShowExcessModal(false);
    setPendingExcessProduct(null);

    setLastCountedProduct({
      ...product,
      counted: product.newCount,
      status: 'excess-approved',
      message: `+1 Stoğa eklenecek (${product.newCount}/${product.stock}+${(excessAdditions[product.id] || 0) + 1})`
    });
  };

  // Fazla ürünü reddet
  const handleExcessReject = () => {
    setShowExcessModal(false);
    setPendingExcessProduct(null);
    toast.info('Fazla ürün sayılmadı');
  };

  // Sayım özeti hesapla
  const getCountSummary = () => {
    const summary = {
      scanned: [],      // Sayılan ürünler
      missing: [],      // Eksik ürünler (sayılmayan veya az sayılan)
      excess: [],       // Fazla ürünler
      matched: [],      // Tam eşleşen
      total: countProducts.length,
      scannedCount: 0,
      missingCount: 0,
      excessCount: 0
    };

    countProducts.forEach(product => {
      const counted = countInventoryData[product.id] || 0;
      const excessAdd = excessAdditions[product.id] || 0;
      const diff = counted - product.stock;

      if (counted > 0) {
        summary.scannedCount++;
        summary.scanned.push({
          ...product,
          counted,
          diff,
          excessToAdd: excessAdd
        });

        if (diff === 0) {
          summary.matched.push({ ...product, counted });
        } else if (diff > 0) {
          summary.excess.push({ ...product, counted, excessAmount: diff, excessToAdd: excessAdd });
          summary.excessCount += diff;
        } else {
          summary.missing.push({ ...product, counted, missingAmount: Math.abs(diff) });
          summary.missingCount += Math.abs(diff);
        }
      } else {
        // Hiç sayılmamış - eksik
        summary.missing.push({ ...product, counted: 0, missingAmount: product.stock });
        summary.missingCount += product.stock;
      }
    });

    return summary;
  };

  // Sayımı tamamla (sadece bu terminalin katılımını - tüm oturumu değil)
  const finalizeCount = async () => {
    if (!countSession?.terminal_id) return;

    setFinalizingCount(true);
    try {
      // Önce son verileri sunucuya gönder
      await fetch(`${API_URL}/inventory-sessions/${countSession.id}/data`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inventory_data: countInventoryData,
          source: `terminal_${user?.id}`
        })
      });

      // Sadece bu terminalin katılımını tamamla (tüm sayımı değil)
      const response = await fetch(`${API_URL}/inventory-session-terminals/${countSession.terminal_id}/complete`, {
        method: 'PUT'
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Sayımınız tamamlandı! Ana onay bekleniyor.');
        setShowCountSummary(false);
        setCountSession(null);
        setCountInvite(null);
        setCountInventoryData({});
        setExcessAdditions({});
        setLastCountedProduct(null);
        setActiveTab('price');
      } else {
        toast.error(data.message || 'Sayım tamamlanamadı!');
      }
    } catch (error) {
      console.error('Sayım tamamlama hatası:', error);
      toast.error('Bir hata oluştu!');
    } finally {
      setFinalizingCount(false);
    }
  };

  // Sayımdan çık
  const exitCount = async () => {
    showConfirmation({
      title: 'Sayımdan Çık',
      message: 'Sayımdan çıkmak istediğinize emin misiniz?',
      confirmText: 'Evet, Çık',
      cancelText: 'İptal',
      type: 'warning',
      onConfirm: async () => {
        // Backend'e çıkış bildir
        if (countSession?.terminal_id) {
          try {
            await fetch(`${API_URL}/inventory-session-terminals/${countSession.terminal_id}/leave`, {
              method: 'PUT'
            });
          } catch (error) {
            console.error('Sayımdan çıkış bildirimi hatası:', error);
          }
        }
        setCountSession(null);
        setCountInvite(null);
        setCountInventoryData({});
        setExcessAdditions({});
        setLastCountedProduct(null);
        setShowCountSummary(false);
        setActiveTab('price');
      }
    });
  };

  // Ürünler tabı açıldığında yükle
  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts();
    }
    if (activeTab === 'transfers') {
      fetchPendingTransfers();
    }
  }, [activeTab]);

  // ===== TRANSFER FONKSİYONLARI =====

  // Bekleyen transferleri getir
  const fetchPendingTransfers = async () => {
    if (!user?.store_id) return;

    setLoadingTransfers(true);
    try {
      const response = await fetch(`${API_URL}/transfers/pending/${user.store_id}`);
      const data = await response.json();
      setPendingTransfers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Transfer yükleme hatası:', error);
      toast.error('Transferler yüklenemedi');
    } finally {
      setLoadingTransfers(false);
    }
  };

  // Transfer detaylarını getir
  const fetchTransferDetails = async (transferId) => {
    try {
      const response = await fetch(`${API_URL}/transfers/${transferId}`);
      const data = await response.json();
      if (data.items) {
        setTransferItems(data.items);
      }
    } catch (error) {
      console.error('Transfer detay hatası:', error);
      toast.error('Transfer detayları yüklenemedi');
    }
  };

  // Transfer seç
  const selectTransfer = async (transfer) => {
    setSelectedTransfer(transfer);
    setScannedTransferItems({});
    setLastScannedTransferItem(null);
    setTransferBarcode('');
    await fetchTransferDetails(transfer.id);
    // Barkod input'una odaklan
    setTimeout(() => {
      if (transferBarcodeRef.current) {
        transferBarcodeRef.current.focus();
      }
    }, 300);
  };

  // Transfer barkod okutma
  const handleTransferBarcodeScan = (e) => {
    if (e.key !== 'Enter' || !transferBarcode.trim()) return;

    const scannedBarcode = transferBarcode.trim();
    const item = transferItems.find(i => i.barcode === scannedBarcode);

    if (!item) {
      // Ürün bu transferde yok
      playCountSound('error');
      setLastScannedTransferItem({
        status: 'notfound',
        barcode: scannedBarcode,
        message: 'Bu ürün transferde yok!'
      });
      toast.error('Bu ürün transferde bulunmuyor!');
    } else {
      const currentScanned = scannedTransferItems[item.product_id] || 0;
      const requiredQty = item.quantity;

      // Son okutulan ürünü listenin en üstüne taşı
      setTransferItems(prev => {
        const idx = prev.findIndex(i => i.product_id === item.product_id);
        if (idx <= 0) return prev;
        const newList = [...prev];
        const [scannedItem] = newList.splice(idx, 1);
        newList.unshift(scannedItem);
        return newList;
      });

      if (currentScanned >= requiredQty) {
        // Zaten tamamlanmış
        playCountSound('error');
        setLastScannedTransferItem({
          ...item,
          status: 'already-complete',
          message: `Bu ürün zaten tamamlandı (${requiredQty}/${requiredQty})`
        });
        toast.warning('Bu ürün zaten tamamlandı!');
      } else {
        // Başarılı okutma
        const newCount = currentScanned + 1;
        setScannedTransferItems(prev => ({
          ...prev,
          [item.product_id]: newCount
        }));

        if (newCount === requiredQty) {
          playCountSound('success');
          setLastScannedTransferItem({
            ...item,
            status: 'complete',
            scanned: newCount,
            message: `✓ Tamamlandı (${newCount}/${requiredQty})`
          });
          toast.success(`${item.product_name || item.name} tamamlandı!`);
        } else {
          playCountSound('success');
          setLastScannedTransferItem({
            ...item,
            status: 'scanning',
            scanned: newCount,
            message: `${newCount}/${requiredQty}`
          });
        }
      }
    }

    setTransferBarcode('');
    setTimeout(() => {
      if (transferBarcodeRef.current) {
        transferBarcodeRef.current.focus();
      }
    }, 100);

    // 5 saniye sonra mesajı temizle
    setTimeout(() => {
      setLastScannedTransferItem(null);
    }, 5000);
  };

  // Tüm ürünler okutuldu mu kontrolü
  const allTransferItemsScanned = () => {
    if (transferItems.length === 0) return false;
    return transferItems.every(item => {
      const scanned = scannedTransferItems[item.product_id] || 0;
      return scanned >= item.quantity;
    });
  };

  // Toplam okutma durumu
  const getTransferScanProgress = () => {
    const totalRequired = transferItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalScanned = Object.values(scannedTransferItems).reduce((sum, count) => sum + count, 0);
    return { totalRequired, totalScanned };
  };

  // Transferi teslim al
  const receiveTransfer = async () => {
    if (!selectedTransfer?.id) return;

    setReceivingTransfer(true);
    try {
      const response = await fetch(`${API_URL}/transfers/${selectedTransfer.id}/receive`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          received_by: user?.id,
          received_by_name: user?.name
        })
      });

      if (response.ok) {
        toast.success('Transfer teslim alındı!');
        setSelectedTransfer(null);
        setTransferItems([]);
        fetchPendingTransfers();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Teslim alma başarısız');
      }
    } catch (error) {
      console.error('Teslim alma hatası:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setReceivingTransfer(false);
    }
  };

  // Ürün detayını aç
  const openProductDetail = (product) => {
    setSelectedProduct(product);
    setStockUpdateValue(product.stock?.toString() || '0');
    setShowProductDetail(true);
  };

  // Stok güncelle
  const updateStock = async () => {
    if (!selectedProduct) return;

    const oldStock = selectedProduct.stock || 0;
    const newStock = parseInt(stockUpdateValue) || 0;

    try {
      const response = await fetch(`${API_URL}/products/${selectedProduct.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...selectedProduct,
          stock: newStock
        })
      });

      if (response.ok) {
        toast.success('Stok güncellendi!');
        // Log kaydet
        logService.log('products', 'stock_adjust',
          `Stok güncellendi: ${selectedProduct.name} - ${oldStock} → ${newStock} (Terminal)`, {
          entity_type: 'product',
          entity_id: selectedProduct.id,
          status: 'success',
          details: {
            product_name: selectedProduct.name,
            barcode: selectedProduct.barcode,
            old_stock: oldStock,
            new_stock: newStock,
            difference: newStock - oldStock,
            source: 'terminal'
          }
        });
        setShowProductDetail(false);
        fetchProducts();
      } else {
        toast.error('Stok güncellenemedi!');
        logService.log('products', 'stock_adjust',
          `Stok güncellenemedi: ${selectedProduct.name}`, {
          status: 'error'
        });
      }
    } catch (error) {
      console.error('Stok güncelleme hatası:', error);
      toast.error('Bir hata oluştu!');
      logService.log('products', 'stock_adjust',
        `Stok güncelleme hatası: ${selectedProduct.name}`, {
        status: 'error',
        details: { error: error.message }
      });
    }
  };

  // Filtrelenmiş ürünler
  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.barcode?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.category?.toLowerCase().includes(productSearch.toLowerCase())
  );

  // Çıkış yap
  const handleLogout = () => {
    showConfirmation({
      title: 'Çıkış Yap',
      message: 'Çıkış yapmak istediğinize emin misiniz?',
      confirmText: 'Evet, Çıkış Yap',
      cancelText: 'İptal',
      type: 'warning',
      onConfirm: () => {
        onLogout();
        navigate('/login');
      }
    });
  };

  // ===== FOTOĞRAF ÇEKİM FONKSİYONLARI =====

  // Fotoğraf isteğini kabul et ve kamerayı aç
  const acceptPhotoRequest = async () => {
    if (!photoRequest?.id) return;

    // Kamera API'si var mı kontrol et
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Bu tarayıcı kamera özelliğini desteklemiyor! HTTPS gerekebilir veya farklı bir tarayıcı deneyin.');
      setPhotoRequest(null);
      return;
    }

    try {
      await fetch(`${API_URL}/terminal-photo-requests/${photoRequest.id}/accept`, {
        method: 'PUT'
      });

      // Kamerayı aç - önce arka kamera dene, olmazsa herhangi bir kamera
      let stream = null;

      // Yöntem 1: Arka kamera
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });
        console.log('Arka kamera açıldı');
      } catch (e) {
        console.log('Arka kamera açılamadı:', e.message);
      }

      // Yöntem 2: Ön kamera
      if (!stream) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user' }
          });
          console.log('Ön kamera açıldı');
        } catch (e) {
          console.log('Ön kamera açılamadı:', e.message);
        }
      }

      // Yöntem 3: Herhangi bir kamera
      if (!stream) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: true });
          console.log('Varsayılan kamera açıldı');
        } catch (e) {
          console.log('Varsayılan kamera açılamadı:', e.message);
          throw e; // Son deneme de başarısız, hatayı fırlat
        }
      }

      if (!stream) {
        throw new Error('Hiçbir kamera açılamadı');
      }

      setPhotoStream(stream);
      setShowPhotoCapture(true);

      // Video elementine stream'i bağla
      setTimeout(() => {
        if (videoRef.current && stream) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(e => console.log('Video play hatası:', e));
        }
      }, 300);
    } catch (error) {
      console.error('Kamera açma hatası:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Kamera izni verilmedi! Lütfen tarayıcı ayarlarından kamera iznini verin.');
      } else if (error.name === 'NotFoundError') {
        toast.error('Kamera bulunamadı! Cihazınızda kamera var mı?');
      } else if (error.name === 'NotReadableError') {
        toast.error('Kamera başka bir uygulama tarafından kullanılıyor.');
      } else if (error.name === 'TypeError') {
        toast.error('Kamera API desteklenmiyor. HTTPS bağlantısı gerekebilir.');
      } else {
        toast.error(`Kamera açılamadı: ${error.message || 'Bilinmeyen hata'}`);
      }
      setPhotoRequest(null);
    }
  };

  // Fotoğraf isteğini reddet
  const rejectPhotoRequest = async () => {
    if (!photoRequest?.id) return;

    try {
      await fetch(`${API_URL}/terminal-photo-requests/${photoRequest.id}/reject`, {
        method: 'PUT'
      });
      setPhotoRequest(null);
      toast.info('Fotoğraf isteği reddedildi.');
    } catch (error) {
      console.error('Red hatası:', error);
    }
  };

  // Fotoğraf çek
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedPhoto(photoDataUrl);
  };

  // Fotoğrafı tekrar çek
  const retakePhoto = () => {
    setCapturedPhoto(null);
  };

  // Fotoğrafı yükle ve gönder
  const uploadPhoto = async () => {
    if (!capturedPhoto || !photoRequest?.id) return;

    setUploadingPhoto(true);
    try {
      // Base64'ü Blob'a çevir
      const response = await fetch(capturedPhoto);
      const blob = await response.blob();

      const formData = new FormData();
      formData.append('photo', blob, `photo_${Date.now()}.jpg`);

      const uploadResponse = await fetch(`${API_URL}/terminal-photo-requests/${photoRequest.id}/upload`, {
        method: 'POST',
        body: formData
      });

      const data = await uploadResponse.json();
      if (data.success) {
        toast.success('Fotoğraf gönderildi!');
        closePhotoCapture();
      } else {
        toast.error('Fotoğraf gönderilemedi!');
      }
    } catch (error) {
      console.error('Fotoğraf yükleme hatası:', error);
      toast.error('Bir hata oluştu!');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Fotoğraf çekimini kapat
  const closePhotoCapture = () => {
    // Stream'i durdur
    if (photoStream) {
      photoStream.getTracks().forEach(track => track.stop());
      setPhotoStream(null);
    }
    setShowPhotoCapture(false);
    setCapturedPhoto(null);
    setPhotoRequest(null);
  };

  return (
    <div className="terminal-container">
      {/* Header */}
      <header className="terminal-header">
        <div className="terminal-logo">
          <span className="terminal-title">El Terminali</span>
        </div>
        <div className="terminal-user">
          <span className="user-store">{user?.store_name || 'Mağaza'}</span>
          <button className="logout-btn" onClick={handleLogout}>
            <Power size={18} />
          </button>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="terminal-tabs">
        <button
          className={`terminal-tab ${activeTab === 'price' ? 'active' : ''}`}
          onClick={() => setActiveTab('price')}
        >
          <Search size={20} className="tab-icon" />
          <span className="tab-text">Sorgula</span>
        </button>
        <button
          className={`terminal-tab ${activeTab === 'cart' ? 'active' : ''}`}
          onClick={() => setActiveTab('cart')}
        >
          <ShoppingCart size={20} className="tab-icon" />
          <span className="tab-text">Sepet</span>
          {cartItemCount > 0 && (
            <span className="t-cart-badge">{cartItemCount}</span>
          )}
        </button>
        <button
          className={`terminal-tab ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          <Package size={20} className="tab-icon" />
          <span className="tab-text">Ürünler</span>
        </button>
        {user?.permissions?.canTransfer === true && (
          <button
            className={`terminal-tab ${activeTab === 'transfers' ? 'active' : ''}`}
            onClick={() => setActiveTab('transfers')}
          >
            <ArrowDownToLine size={20} className="tab-icon" />
            <span className="tab-text">Transfer</span>
            {pendingTransfers.length > 0 && (
              <span className="t-cart-badge">{pendingTransfers.length}</span>
            )}
          </button>
        )}
        {(countInvite || countSession) && (
          <button
            className={`terminal-tab count-tab ${activeTab === 'count' ? 'active' : ''}`}
            onClick={() => setActiveTab('count')}
          >
            <ClipboardList size={20} className="tab-icon" />
            <span className="tab-text">Sayım</span>
            {countInvite && <span className="count-badge">!</span>}
          </button>
        )}
      </nav>

      {/* Main Content */}
      <main className="terminal-content">
        {/* Fiyat Sorgulama */}
        {activeTab === 'price' && (
          <div className="price-check-panel">
            <form onSubmit={searchByBarcode} className="barcode-form">
              <div className="barcode-input-wrapper">
                <div className="input-container">
                  <input
                    ref={barcodeInputRef}
                    type="text"
                    placeholder="Barkod okutun veya girin..."
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    className="barcode-input"
                    autoComplete="off"
                  />
                  {(barcode || searchedProduct) && (
                    <button type="button" className="clear-btn" onClick={clearSearch}>
                      ✕
                    </button>
                  )}
                </div>
                <button type="submit" className="search-btn" disabled={searching}>
                  {searching ? '...' : <Search size={22} />}
                </button>
              </div>
            </form>

            {searchedProduct ? (
              <div className="product-result">
                <div className="product-image-container">
                  {searchedProduct.image ? (
                    <img
                      src={`${IMAGE_HOST}${searchedProduct.image}`}
                      alt={searchedProduct.name}
                      className="product-image"
                    />
                  ) : (
                    <div className="no-image"><Camera size={48} strokeWidth={1} /></div>
                  )}
                </div>

                <div className="product-info">
                  <h2 className="product-name">{searchedProduct.name}</h2>
                  <p className="product-barcode">Barkod: {searchedProduct.barcode}</p>
                  <p className="product-category">{searchedProduct.category}</p>
                </div>

                <div className="price-display">
                  <span className="price-label">Satış Fiyatı</span>
                  <span className="price-value">
                    {displayPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                  </span>
                </div>

                <div className="product-details">
                  <div className="detail-row">
                    <span className="detail-label">Stok</span>
                    <span className={`detail-value ${searchedProduct.stock <= 0 ? 'out-of-stock' : ''}`}>
                      {searchedProduct.stock || 0} Adet
                    </span>
                  </div>
                  {searchedProduct.weight && (
                    <div className="detail-row">
                      <span className="detail-label">Ağırlık</span>
                      <span className="detail-value">{searchedProduct.weight} gr</span>
                    </div>
                  )}
                  {searchedProduct.metal_type && (
                    <div className="detail-row">
                      <span className="detail-label">Metal</span>
                      <span className="detail-value">{searchedProduct.metal_type}</span>
                    </div>
                  )}
                </div>

                {/* Sepete Ekle Butonu */}
                <button
                  className="add-to-cart-btn"
                  onClick={() => addToCart(searchedProduct)}
                  disabled={searchedProduct.stock <= 0}
                >
                  <ShoppingCart size={18} /> Sepete Ekle
                </button>
              </div>
            ) : (
              <div className="empty-result">
                <Smartphone size={48} strokeWidth={1} className="empty-icon" />
                <p>Barkod okutarak ürün fiyatını sorgulayın</p>
              </div>
            )}

            {/* Güncel Fiyatlar */}
            <div className="live-prices">
              <div className="t-price-item gold">
                <span className="t-price-type">Altın</span>
                <div className="t-price-rates">
                  <div className="t-rate-row">
                    <span className="t-rate-label">Alış</span>
                    <span className="t-rate-value buy">{parseFloat(prices.altin_alis || 0).toLocaleString('tr-TR')} ₺</span>
                  </div>
                  <div className="t-rate-row">
                    <span className="t-rate-label">Satış</span>
                    <span className="t-rate-value sell">{parseFloat(prices.altin_satis || 0).toLocaleString('tr-TR')} ₺</span>
                  </div>
                </div>
              </div>
              <div className="t-price-item silver">
                <span className="t-price-type">Gümüş</span>
                <div className="t-price-rates">
                  <div className="t-rate-row">
                    <span className="t-rate-label">Alış</span>
                    <span className="t-rate-value buy">{parseFloat(prices.gumus_alis || 0).toLocaleString('tr-TR')} ₺</span>
                  </div>
                  <div className="t-rate-row">
                    <span className="t-rate-label">Satış</span>
                    <span className="t-rate-value sell">{parseFloat(prices.gumus_satis || 0).toLocaleString('tr-TR')} ₺</span>
                  </div>
                </div>
              </div>
              <div className="t-price-item usd">
                <span className="t-price-type">$ USD</span>
                <div className="t-price-rates">
                  <div className="t-rate-row">
                    <span className="t-rate-label">Alış</span>
                    <span className="t-rate-value buy">{parseFloat(prices.USD_alis || prices.USD || 0).toLocaleString('tr-TR')} ₺</span>
                  </div>
                  <div className="t-rate-row">
                    <span className="t-rate-label">Satış</span>
                    <span className="t-rate-value sell">{parseFloat(prices.USD_satis || prices.USD || 0).toLocaleString('tr-TR')} ₺</span>
                  </div>
                </div>
              </div>
              <div className="t-price-item eur">
                <span className="t-price-type">€ EUR</span>
                <div className="t-price-rates">
                  <div className="t-rate-row">
                    <span className="t-rate-label">Alış</span>
                    <span className="t-rate-value buy">{parseFloat(prices.EUR_alis || prices.EUR || 0).toLocaleString('tr-TR')} ₺</span>
                  </div>
                  <div className="t-rate-row">
                    <span className="t-rate-label">Satış</span>
                    <span className="t-rate-value sell">{parseFloat(prices.EUR_satis || prices.EUR || 0).toLocaleString('tr-TR')} ₺</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sepet */}
        {activeTab === 'cart' && (
          <div className="t-cart-panel">
            {cart.length === 0 ? (
              <div className="t-empty-cart">
                <ShoppingCart size={64} strokeWidth={1} className="empty-icon" />
                <p>Sepetiniz boş</p>
                <button className="t-go-scan-btn" onClick={() => setActiveTab('price')}>
                  Ürün Tara
                </button>
              </div>
            ) : (
              <>
                <div className="t-cart-items">
                  {cart.map(item => (
                    <div key={item.id} className="t-cart-item">
                      <div className="t-cart-item-thumb">
                        {item.image ? (
                          <img src={`${IMAGE_HOST}${item.image}`} alt="" loading="lazy" />
                        ) : (
                          <Package size={22} strokeWidth={1.5} />
                        )}
                      </div>
                      <div className="t-cart-item-info">
                        <span className="t-cart-item-name">{item.name}</span>
                        <span className="t-cart-item-price">
                          {item.sell_price.toLocaleString('tr-TR')} ₺
                        </span>
                      </div>
                      <div className="t-cart-item-qty">
                        <button
                          className="t-qty-btn"
                          onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                        >
                          −
                        </button>
                        <span className="t-qty-value">{item.quantity}</span>
                        <button
                          className="t-qty-btn"
                          onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                        >
                          +
                        </button>
                      </div>
                      <button
                        className="t-remove-item-btn"
                        onClick={() => removeFromCart(item.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="t-cart-summary">
                  {campaignDiscount > 0 && (
                    <div className="t-cart-total" style={{ color: '#f59e0b' }}>
                      <span>🏷️ {appliedCampaign?.name || 'Kampanya İndirimi'}</span>
                      <span className="t-total-amount">
                        -{campaignDiscount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                      </span>
                    </div>
                  )}
                  <div className="t-cart-total">
                    <span>Toplam ({cartItemCount} ürün)</span>
                    <span className="t-total-amount">
                      {cartTotalAfterDiscount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                    </span>
                  </div>

                  <div className="t-cart-actions">
                    <button className="clear-cart-btn" onClick={clearCart}>
                      Temizle
                    </button>
                    <div className="send-cart-wrapper">
                      <button
                        className={`staff-quick-select ${selectedStaff ? 'has-staff' : ''}`}
                        onClick={() => { setShowStaffModal(true); fetchSalesStaff(); }}
                      >
                        <User size={14} className="staff-quick-icon" />
                        <span className="staff-quick-name">
                          {selectedStaff ? selectedStaff.name : 'Sorumlu Seç'}
                        </span>
                        <span className="staff-quick-arrow">▼</span>
                      </button>
                      <button
                        className="send-cart-btn"
                        onClick={sendCartToCashier}
                        disabled={sendingCart}
                      >
                        {sendingCart ? 'Gönderiliyor...' : <><Send size={16} /> Kasiyere Gönder</>}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Ürün Listesi */}
        {activeTab === 'products' && (
          <div className="products-panel">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Ürün ara (ad, barkod, kategori)..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                className="search-input"
              />
            </div>

            {loadingProducts ? (
              <div className="loading-state">Yükleniyor...</div>
            ) : (
              <div className="products-list">
                {filteredProducts.length === 0 ? (
                  <div className="empty-state">Ürün bulunamadı</div>
                ) : (
                  filteredProducts.map(product => (
                    <div
                      key={product.id}
                      className="product-item"
                      onClick={() => openProductDetail(product)}
                    >
                      <div className="product-thumb">
                        {product.image ? (
                          <img src={`${IMAGE_HOST}${product.image}`} alt="" loading="lazy" />
                        ) : (
                          <Package size={22} strokeWidth={1.5} />
                        )}
                      </div>
                      <div className="product-info">
                        <span className="product-name">{product.name}</span>
                        <span className="product-barcode">{product.barcode}</span>
                      </div>
                      <div className="product-price">
                        {calculateGoldPrice(product).toLocaleString('tr-TR')} ₺
                      </div>
                      <div className={`product-stock ${product.stock <= 0 ? 'out' : ''}`}>
                        {product.stock || 0}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Sayım Paneli */}
        {activeTab === 'count' && (
          <div className="count-panel">
            {/* Davet bekliyor */}
            {countInvite && !countSession && (
              <div className="count-invite">
                <ClipboardList size={64} strokeWidth={1} className="invite-icon" />
                <h3>Sayım Daveti</h3>
                <p>Mağazanızda bir sayım başlatıldı. Katılmak ister misiniz?</p>
                <button className="accept-invite-btn" onClick={acceptCountInvite}>
                  <Check size={18} /> Sayıma Katıl
                </button>
              </div>
            )}

            {/* Aktif sayım oturumu */}
            {countSession && (
              <>
                {/* Barkod giriş alanı */}
                <div className="count-barcode-section">
                  <input
                    ref={countBarcodeRef}
                    type="text"
                    placeholder="Barkod okutun veya girin..."
                    value={countBarcode}
                    onChange={(e) => setCountBarcode(e.target.value)}
                    onKeyDown={handleCountBarcode}
                    className="count-barcode-input"
                    autoComplete="off"
                  />
                  <div className="count-stats">
                    <span className="count-stat">{Object.keys(countInventoryData).length} ürün sayıldı</span>
                  </div>
                </div>

                {/* Son okutulan ürün bilgisi */}
                {lastCountedProduct && (
                  <div className={`count-product-result ${lastCountedProduct.status}`}>
                    {lastCountedProduct.status !== 'notfound' ? (
                      <>
                        <div className="count-product-image">
                          {lastCountedProduct.image ? (
                            <img src={`${IMAGE_HOST}${lastCountedProduct.image}`} alt="" />
                          ) : (
                            <Package size={48} strokeWidth={1} className="no-img" />
                          )}
                        </div>
                        <div className="count-product-info">
                          <h4 className="count-product-name">{lastCountedProduct.name}</h4>
                          <p className="count-product-barcode">{lastCountedProduct.barcode}</p>
                          <div className="count-product-details">
                            {lastCountedProduct.category && (
                              <span className="count-detail">{lastCountedProduct.category}</span>
                            )}
                            {lastCountedProduct.weight && (
                              <span className="count-detail">{lastCountedProduct.weight} gr</span>
                            )}
                          </div>
                        </div>
                        <div className={`count-product-status ${lastCountedProduct.status}`}>
                          <span className="status-message">{lastCountedProduct.message}</span>
                        </div>
                      </>
                    ) : (
                      <div className="count-notfound">
                        <AlertTriangle size={48} className="notfound-icon" />
                        <span className="notfound-text">{lastCountedProduct.name}</span>
                        <span className="notfound-message">{lastCountedProduct.message}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Boş durum */}
                {!lastCountedProduct && (
                  <div className="count-empty-state">
                    <Smartphone size={64} strokeWidth={1} className="empty-icon" />
                    <p>Barkod okutarak sayım yapın</p>
                    <p className="empty-hint">Her okutmada ürün sayısı 1 artar</p>
                  </div>
                )}

                {/* Sayım butonları */}
                <div className="count-actions">
                  <button className="exit-count-btn" onClick={exitCount}>
                    <X size={16} /> Çık
                  </button>
                  <button
                    className="finish-count-btn"
                    onClick={() => setShowCountSummary(true)}
                    disabled={Object.keys(countInventoryData).length === 0}
                  >
                    <Check size={16} /> Sayımı Bitir
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Transfer Teslim Alma Paneli */}
        {activeTab === 'transfers' && (
          <div className="transfers-panel">
            {!selectedTransfer ? (
              <>
                <div className="transfers-header">
                  <h3><ArrowDownToLine size={20} /> Bekleyen Transferler</h3>
                  <button className="refresh-btn" onClick={fetchPendingTransfers} disabled={loadingTransfers}>
                    <RefreshCw size={18} />
                  </button>
                </div>

                {loadingTransfers ? (
                  <div className="loading-state">Yükleniyor...</div>
                ) : pendingTransfers.length === 0 ? (
                  <div className="empty-state">
                    <PackageOpen size={48} strokeWidth={1} className="empty-icon" />
                    <p>Bekleyen transfer yok</p>
                  </div>
                ) : (
                  <div className="transfers-list">
                    {pendingTransfers.map(transfer => (
                      <div
                        key={transfer.id}
                        className="transfer-item"
                        onClick={() => selectTransfer(transfer)}
                      >
                        <Package size={28} className="transfer-icon" />
                        <div className="transfer-info">
                          <span className="transfer-code">{transfer.transfer_code}</span>
                          <span className="transfer-from">
                            {transfer.from_store_name || `Mağaza #${transfer.from_store_id}`}
                          </span>
                          <span className="transfer-date">
                            {new Date(transfer.created_at).toLocaleDateString('tr-TR')}
                          </span>
                        </div>
                        <div className="transfer-arrow">→</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="transfer-detail-header">
                  <button className="back-btn" onClick={() => { setSelectedTransfer(null); setTransferItems([]); setScannedTransferItems({}); }}>
                    <ChevronLeft size={18} /> Geri
                  </button>
                  <h3>{selectedTransfer.transfer_code}</h3>
                </div>

                <div className="transfer-detail-info">
                  <div className="info-row">
                    <span className="info-label">Gönderen:</span>
                    <span className="info-value">{selectedTransfer.from_store_name || `Mağaza #${selectedTransfer.from_store_id}`}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Tarih:</span>
                    <span className="info-value">{new Date(selectedTransfer.created_at).toLocaleString('tr-TR')}</span>
                  </div>
                </div>

                {/* Barkod Okutma Alanı */}
                <div className="transfer-scan-section">
                  <input
                    ref={transferBarcodeRef}
                    type="text"
                    placeholder="Ürün barkodunu okutun..."
                    value={transferBarcode}
                    onChange={(e) => setTransferBarcode(e.target.value)}
                    onKeyDown={handleTransferBarcodeScan}
                    className="transfer-barcode-input"
                    autoComplete="off"
                  />
                  <div className="scan-progress">
                    <div className="progress-text">
                      {getTransferScanProgress().totalScanned} / {getTransferScanProgress().totalRequired} ürün okutuldu
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${(getTransferScanProgress().totalScanned / getTransferScanProgress().totalRequired) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Son Okutulan Ürün Bilgisi */}
                {lastScannedTransferItem && (
                  <div className={`transfer-scan-result ${lastScannedTransferItem.status}`}>
                    {lastScannedTransferItem.status === 'notfound' ? (
                      <div className="scan-notfound">
                        <AlertTriangle size={24} className="notfound-icon" />
                        <span className="notfound-barcode">{lastScannedTransferItem.barcode}</span>
                        <span className="notfound-message">{lastScannedTransferItem.message}</span>
                      </div>
                    ) : (
                      <div className="scan-found">
                        <div className="scan-product-thumb">
                          {lastScannedTransferItem.image ? (
                            <img src={`${IMAGE_HOST}${lastScannedTransferItem.image}`} alt="" />
                          ) : (
                            <Package size={24} strokeWidth={1.5} />
                          )}
                        </div>
                        <div className="scan-product-info">
                          <span className="scan-product-name">{lastScannedTransferItem.product_name || lastScannedTransferItem.name}</span>
                          <span className={`scan-status ${lastScannedTransferItem.status}`}>{lastScannedTransferItem.message}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="transfer-items-list">
                  <h4>Ürünler ({transferItems.length})</h4>
                  {transferItems.map(item => {
                    const scannedCount = scannedTransferItems[item.product_id] || 0;
                    const isComplete = scannedCount >= item.quantity;
                    return (
                      <div key={item.id} className={`transfer-product-item ${isComplete ? 'scanned-complete' : scannedCount > 0 ? 'scanned-partial' : ''}`}>
                        <div className="product-thumb">
                          {item.image ? (
                            <img src={`${IMAGE_HOST}${item.image}`} alt="" />
                          ) : (
                            <Package size={24} strokeWidth={1.5} />
                          )}
                        </div>
                        <div className="product-info">
                          <span className="product-name">{item.product_name || item.name}</span>
                          <span className="product-barcode">{item.barcode}</span>
                        </div>
                        <div className={`product-scan-status ${isComplete ? 'complete' : ''}`}>
                          {isComplete ? <Check size={16} /> : `${scannedCount}/${item.quantity}`}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="transfer-actions">
                  <button
                    className="receive-btn"
                    onClick={receiveTransfer}
                    disabled={receivingTransfer || !allTransferItemsScanned()}
                  >
                    {receivingTransfer ? 'İşleniyor...' : allTransferItemsScanned() ? <><Check size={18} /> Teslim Al</> : `Tüm ürünleri okutun (${getTransferScanProgress().totalScanned}/${getTransferScanProgress().totalRequired})`}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Fazla Ürün Modal */}
        {showExcessModal && pendingExcessProduct && (
          <div className="modal-overlay">
            <div className="modal-content excess-modal">
              <div className="modal-header">
                <h3><AlertTriangle size={20} /> Fazla Ürün!</h3>
              </div>
              <div className="modal-body">
                <div className="excess-product-info">
                  {pendingExcessProduct.image && (
                    <img
                      src={`${IMAGE_HOST}${pendingExcessProduct.image}`}
                      alt=""
                      className="excess-product-img"
                    />
                  )}
                  <h4>{pendingExcessProduct.name}</h4>
                  <p className="excess-barcode">{pendingExcessProduct.barcode}</p>
                </div>
                <div className="excess-warning">
                  <p>Sistemde <strong>{pendingExcessProduct.stock}</strong> adet kayıtlı</p>
                  <p>Sayılan: <strong>{pendingExcessProduct.newCount}</strong> adet</p>
                  <p className="excess-diff">+{pendingExcessProduct.excessAmount} fazla</p>
                </div>
                <p className="excess-question">Bu fazla ürünü stoğa eklemek ister misiniz?</p>
              </div>
              <div className="modal-footer">
                <button className="cancel-btn" onClick={handleExcessReject}>
                  Hayır, Sayma
                </button>
                <button className="save-btn" onClick={handleExcessApprove}>
                  Evet, Stoğa Ekle
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sayım Özeti Modal */}
        {showCountSummary && (
          <div className="modal-overlay">
            <div className="modal-content summary-modal">
              <div className="modal-header">
                <h3><ClipboardList size={20} /> Sayım Özeti</h3>
                <button className="close-btn" onClick={() => setShowCountSummary(false)}><X size={18} /></button>
              </div>
              <div className="modal-body">
                {(() => {
                  const summary = getCountSummary();
                  return (
                    <>
                      {/* Genel İstatistikler */}
                      <div className="summary-stats">
                        <div className="summary-stat">
                          <span className="stat-value">{summary.scannedCount}</span>
                          <span className="stat-label">Sayılan Ürün</span>
                        </div>
                        <div className="summary-stat matched">
                          <span className="stat-value">{summary.matched.length}</span>
                          <span className="stat-label">Tam Eşleşen</span>
                        </div>
                        <div className="summary-stat excess">
                          <span className="stat-value">{summary.excess.length}</span>
                          <span className="stat-label">Fazla</span>
                        </div>
                        <div className="summary-stat missing">
                          <span className="stat-value">{summary.missing.length}</span>
                          <span className="stat-label">Eksik</span>
                        </div>
                      </div>

                      {/* Eksik Ürünler */}
                      {summary.missing.length > 0 && (
                        <div className="summary-section missing">
                          <h4>❌ Eksik Ürünler ({summary.missingCount} adet)</h4>
                          <p className="section-note">Bu ürünler stoktan düşürülecek</p>
                          <div className="summary-list">
                            {summary.missing.slice(0, 10).map(item => (
                              <div key={item.id} className="summary-item">
                                <span className="item-name">{item.name}</span>
                                <span className="item-diff">
                                  {item.counted}/{item.stock} (-{item.missingAmount})
                                </span>
                              </div>
                            ))}
                            {summary.missing.length > 10 && (
                              <p className="more-items">+{summary.missing.length - 10} ürün daha...</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Fazla Ürünler */}
                      {summary.excess.length > 0 && (
                        <div className="summary-section excess">
                          <h4>➕ Fazla Ürünler ({summary.excessCount} adet)</h4>
                          <p className="section-note">Bu ürünler stoğa eklenecek</p>
                          <div className="summary-list">
                            {summary.excess.map(item => (
                              <div key={item.id} className="summary-item">
                                <span className="item-name">{item.name}</span>
                                <span className="item-diff">
                                  {item.counted}/{item.stock} (+{item.excessToAdd})
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Eşleşenler */}
                      {summary.matched.length > 0 && (
                        <div className="summary-section matched">
                          <h4>✓ Tam Eşleşen ({summary.matched.length} ürün)</h4>
                          <div className="summary-list compact">
                            {summary.matched.slice(0, 5).map(item => (
                              <div key={item.id} className="summary-item">
                                <span className="item-name">{item.name}</span>
                                <span className="item-diff">{item.counted}</span>
                              </div>
                            ))}
                            {summary.matched.length > 5 && (
                              <p className="more-items">+{summary.matched.length - 5} ürün daha...</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Uyarı */}
                      {summary.missingCount > 0 && (
                        <div className="summary-warning">
                          ⚠️ Eksik ürünler stoktan düşürülecek. Bu işlem geri alınamaz!
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <button className="cancel-btn" onClick={() => setShowCountSummary(false)}>
                  Geri Dön
                </button>
                <button
                  className="save-btn finalize-btn"
                  onClick={finalizeCount}
                  disabled={finalizingCount}
                >
                  {finalizingCount ? 'İşleniyor...' : <><Check size={16} /> Onayla ve Stokları Güncelle</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Satış Sorumlusu Modal */}
      {showStaffModal && (
        <div className="modal-overlay">
          <div className="modal staff-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Satış Sorumlusu Seç</h2>
              <button className="close-btn" onClick={() => setShowStaffModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {loadingStaff ? (
                <div className="loading-state">Yükleniyor...</div>
              ) : salesStaffList.length === 0 ? (
                <div className="empty-state">Satış sorumlusu bulunamadı</div>
              ) : (
                <div className="staff-list">
                  {salesStaffList.map(staff => {
                    const isSelected = selectedStaff?.id === staff.id;
                    return (
                      <div key={staff.id} className={`staff-item ${isSelected ? 'selected' : ''}`} onClick={() => { setSelectedStaff(staff); setShowStaffModal(false); toast.success(`${staff.name} seçildi`); }}>
                        <div className={`staff-avatar ${staff.photo_url ? 'has-photo' : ''}`}>
                          {isSelected ? <Check size={24} strokeWidth={3} /> : staff.photo_url ? <img src={`${API_HOST}${staff.photo_url}`} alt={staff.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : staff.name.charAt(0)}
                        </div>
                        <div className="staff-info"><span className="staff-name">{staff.name}</span></div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ürün Detay Modal */}
      {showProductDetail && selectedProduct && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Ürün Detayı</h3>
              <button className="close-btn" onClick={() => setShowProductDetail(false)}><X size={18} /></button>
            </div>

            <div className="modal-body">
              <div className="detail-image">
                {selectedProduct.image ? (
                  <img src={`${IMAGE_HOST}${selectedProduct.image}`} alt="" />
                ) : (
                  <Package size={64} strokeWidth={1} className="no-image-large" />
                )}
              </div>

              <h4 className="detail-name">{selectedProduct.name}</h4>
              <p className="detail-barcode">Barkod: {selectedProduct.barcode}</p>

              <div className="detail-grid">
                <div className="detail-item">
                  <span className="label">Kategori</span>
                  <span className="value">{selectedProduct.category || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Fiyat</span>
                  <span className="value">
                    {calculateGoldPrice(selectedProduct).toLocaleString('tr-TR')} ₺
                    {selectedProduct.metal_type === 'gold' && selectedProduct.gold_karat && (
                      <small style={{fontSize: '10px', opacity: 0.7, marginLeft: '4px'}}>({selectedProduct.gold_karat} Ayar)</small>
                    )}
                    {(selectedProduct.currency === 'USD' || selectedProduct.price_currency === 'USD') && (
                      <small style={{fontSize: '10px', opacity: 0.7, marginLeft: '4px'}}>(${parseFloat(selectedProduct.sell_price).toFixed(2)})</small>
                    )}
                    {(selectedProduct.currency === 'EUR' || selectedProduct.price_currency === 'EUR') && (
                      <small style={{fontSize: '10px', opacity: 0.7, marginLeft: '4px'}}>(€{parseFloat(selectedProduct.sell_price).toFixed(2)})</small>
                    )}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="label">Metal</span>
                  <span className="value">{selectedProduct.metal_type || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Ağırlık</span>
                  <span className="value">{selectedProduct.weight || '-'} gr</span>
                </div>
              </div>

              <div className="stock-update-section">
                <label>Stok Miktarı</label>
                <div className="stock-input-group">
                  <button
                    className="stock-btn minus"
                    onClick={() => setStockUpdateValue(Math.max(0, parseInt(stockUpdateValue || 0) - 1).toString())}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    value={stockUpdateValue}
                    onChange={(e) => setStockUpdateValue(e.target.value)}
                    className="stock-input"
                  />
                  <button
                    className="stock-btn plus"
                    onClick={() => setStockUpdateValue((parseInt(stockUpdateValue || 0) + 1).toString())}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowProductDetail(false)}>
                İptal
              </button>
              <button className="add-cart-modal-btn" onClick={() => { addToCart(selectedProduct); setShowProductDetail(false); }}>
                <ShoppingCart size={16} /> Sepete Ekle
              </button>
              <button className="save-btn" onClick={updateStock}>
                Stok Güncelle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fotoğraf İsteği Bildirimi */}
      {photoRequest && !showPhotoCapture && (
        <div className="photo-request-notification">
          <div className="photo-request-content">
            <Camera size={32} className="photo-request-icon" />
            <div className="photo-request-info">
              <h4>Fotoğraf Çekim İsteği</h4>
              <p>{photoRequest.description || 'Tamirat ürünü için fotoğraf çekin'}</p>
            </div>
            <div className="photo-request-actions">
              <button className="accept-btn" onClick={acceptPhotoRequest}>
                <Check size={16} /> Kabul Et
              </button>
              <button className="reject-btn" onClick={rejectPhotoRequest}>
                <X size={16} /> Reddet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fotoğraf Çekim Modal */}
      {showPhotoCapture && (
        <div className="photo-capture-overlay">
          <div className="photo-capture-container">
            <div className="photo-capture-header">
              <h3><Camera size={22} /> Fotoğraf Çek</h3>
              <button className="close-btn" onClick={closePhotoCapture}><X size={20} /></button>
            </div>

            <div className="photo-capture-body">
              {!capturedPhoto ? (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="camera-preview"
                    onLoadedMetadata={(e) => e.target.play()}
                  />
                  <button className="capture-btn" onClick={capturePhoto}>
                    <Camera size={20} /> Fotoğraf Çek
                  </button>
                </>
              ) : (
                <>
                  <img src={capturedPhoto} alt="Çekilen fotoğraf" className="captured-preview" />
                  <div className="photo-actions">
                    <button className="retake-btn" onClick={retakePhoto} disabled={uploadingPhoto}>
                      <RefreshCw size={16} /> Tekrar Çek
                    </button>
                    <button className="send-btn" onClick={uploadPhoto} disabled={uploadingPhoto}>
                      {uploadingPhoto ? 'Gönderiliyor...' : <><Check size={16} /> Gönder</>}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Gizli canvas - fotoğraf yakalamak için */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
        </div>
      )}

      {/* Genel Onay Modal (window.confirm yerine) */}
      {showConfirmModal && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal confirm-modal" onClick={e => e.stopPropagation()} style={{
            maxWidth: '420px',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div className="modal-header" style={{
              background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                         confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                         'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={28} />
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{confirmModalConfig.title}</h2>
            </div>
            <div className="modal-body" style={{ padding: '24px', background: '#fff' }}>
              <p style={{ margin: 0, fontSize: '15px', color: '#374151', lineHeight: '1.6' }}>
                {confirmModalConfig.message}
              </p>
            </div>
            <div className="modal-footer" style={{
              padding: '16px 24px',
              background: '#f9fafb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.cancelText}
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  confirmModalConfig.onConfirm();
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                             confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                             'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TerminalPanel;
